import * as styles from './FileEditDialog.module.less'
import { ICreateFolderCmd, IUpdateSingleFileCmd, OwnerType } from "@/twapp/server/FileEndpoint";
import { TromanDialog, TromanDialogBody, TromanDialogBodySection, TromanDialogFooter, TromanDialogTitleHeader } from "../../TromanDialog";
import { IFileRoot, isFolder } from "../model/fileModel";
import { FormikErrors, useFormik } from "formik";
import { ButtonVariants, TromanButton } from "../../Buttons";
import { isValidFileNameWithoutExtension } from '../model/fileValidation';
import _ from 'lodash';
import { WithOptional } from '@/common/typeHelpers';
import { IEditForm, IPreparedEdit } from './fileEdit';

function validateForm(editing: IPreparedEdit, values: IEditForm) {
    const result: FormikErrors<IEditForm> = {};
    if (!isValidFileNameWithoutExtension(values.name)) {
        result.name = 'Ogiltligt namn';
    } else if (editing.existingNamesLC.has((values.name + editing.extension).toLowerCase())) {
        result.name = 'Namnet används redan';
    }
    return result;
}

function createUpdateCmd(root: IFileRoot, editing: IPreparedEdit, values: IEditForm): IUpdateSingleFileCmd|undefined {
    if (!editing.entry) {
        return;
    }

    return {
        OwnerId: root.ownerId,
        OwnerType: root.ownerType,
        FileId: editing.entry.id,
        Name: values.name + editing.extension,
        ShowPolitiker: values.showPolitiker,
        ShowPublik: values.showPublik,
    };
}

function createAddFolderCmd(root: IFileRoot, editing: IPreparedEdit, values: IEditForm): ICreateFolderCmd {
    return {
        OwnerId: root.ownerId,
        OwnerType: root.ownerType,
        Path: isFolder(editing.container) ? `${editing.container.fullPath}/${values.name}` : values.name,
    };
}

function createResult(root: IFileRoot, editing: IPreparedEdit, values: IEditForm): IFileEditResult | undefined {
    if (editing.entry) {
        return { update: createUpdateCmd(root, editing, values) };
    } else if (!editing.isFile) {
        return { createFolder: createAddFolderCmd(root, editing, values) };
    }
}

export interface IFileEditResult {
    update?: IUpdateSingleFileCmd;
    createFolder?: ICreateFolderCmd;
}

export interface IFileEditProps {
    root: IFileRoot,
    hasPublish: boolean;
    busy: boolean;
    editing: IPreparedEdit,
    onClose: (cmd?: IFileEditResult) => void;
}

function FileForm({root, hasPublish, editing, busy, onClose}: IFileEditProps) {
    const form = useFormik({
        initialValues: editing.initial,
        enableReinitialize: true,
        onSubmit: x => onClose(createResult(root, editing, x)),
        validate: x => validateForm(editing, x),
    });

    return (
        <form className={styles.root} onSubmit={form.handleSubmit}>
            <TromanDialogTitleHeader title={editing.isFile ? "Redigera dokument" : editing.entry ? "Redigera mapp" : "Skapa mapp"}/>
            <TromanDialogBody>
                <TromanDialogBodySection>
                    <div className={`form-group ${form.errors.name ? 'has-error' : ''}`}>
                        <label className="control-label">Namn</label>
                        {
                            editing.isFile ? (
                                <div className="input-group">
                                    <input type="text" className="form-control" value={form.values.name} name="name" onChange={form.handleChange} />
                                    <span className="input-group-addon">{editing.extension}</span>
                                </div>
                            ) : (
                                <input type="text" className="form-control" value={form.values.name} name="name" onChange={form.handleChange} />
                            )
                        }
                    </div>
                    { hasPublish && editing.isFile && (
                        <div className='form-group'>
                            <div className="checkbox">
                                <label>
                                    <input type="checkbox" checked={form.values.showPolitiker} name="showPolitiker" onChange={form.handleChange}/>
                                    Publicera på Troman Politiker
                                </label>
                            </div>
                            <div className="checkbox">
                                <label>
                                    <input type="checkbox" checked={form.values.showPublik} name="showPublik" onChange={form.handleChange}/>
                                    Publicera på Troman Publik
                                </label>
                            </div>
                        </div>
                    )}
                </TromanDialogBodySection>
                {
                    editing.entry && editing.isFile && (
                        <TromanDialogBodySection>
                            <table className={styles.details}>
                                <tbody>
                                    <tr>
                                        <td>Ändrad:</td>
                                        <td>{editing.entry.source.UserModifiedTimestamp?.toLocaleString('sv-SE')}</td>
                                    </tr>
                                    <tr>
                                        <td>Uppladdad:</td>
                                        <td>{editing.entry.source.CreatedTimestamp.toLocaleString('sv-SE')}</td>
                                    </tr>
                                    <tr>
                                        <td>Uppladdad av:</td>
                                        <td>{editing.user?.displayName}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </TromanDialogBodySection>
                    )
                }
            </TromanDialogBody>
            <TromanDialogFooter>
                <TromanButton type="submit" disabled={busy} variant={ButtonVariants.Success} text="Spara"/>
                <TromanButton text="Avbryt" disabled={busy} onClick={() => onClose()}/>
            </TromanDialogFooter>
        </form>
    )
}

export function FileEditDialog({editing, ...props}: WithOptional<IFileEditProps, "editing">) {
    return (
        <TromanDialog open={!!editing}>
            {
                editing && <FileForm editing={editing} {...props}/>
            }
        </TromanDialog>
    )
}
